import React, { useState, useEffect, useCallback } from 'react';
import { fetchProducts, ShopifyProduct } from '@/services/shopify';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import CustomLoader from '@/components/UI/CustomLoader';
import { useNavigate } from 'react-router-dom';
import { useCartStore } from '@/stores/cartStore';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import useEmblaCarousel from 'embla-carousel-react';
export const FeaturedProductsCarousel = () => {
  const [featuredProducts, setFeaturedProducts] = useState<ShopifyProduct[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();
  const addItem = useCartStore(state => state.addItem);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    duration: 30
  });
  useEffect(() => {
    const loadFeaturedProducts = async () => {
      try {
        setIsLoading(true);
        
        // Fetch all products with "prints" tag
        const allPrints = await fetchProducts(50, 'tag:prints');
        
        // Fisher-Yates shuffle for better randomization on every load
        const shuffled = [...allPrints];
        for (let i = shuffled.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        const selectedProducts = shuffled.slice(0, 4);
        
        setFeaturedProducts(selectedProducts);
      } catch (error) {
        console.error('Error loading featured products:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadFeaturedProducts();
  }, []);
  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);
  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, onSelect]);
  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);
  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);
  const scrollTo = useCallback((index: number) => {
    if (emblaApi) emblaApi.scrollTo(index);
  }, [emblaApi]);
  const handleAddToCart = (product: ShopifyProduct) => {
    const variant = product.node.variants.edges[0]?.node;
    if (!variant) return;
    addItem({
      product: product,
      variantId: variant.id,
      variantTitle: variant.title,
      price: {
        amount: variant.price.amount,
        currencyCode: variant.price.currencyCode
      },
      quantity: 1,
      selectedOptions: variant.selectedOptions
    });
    toast.success('Added to cart');
  };
  if (isLoading) {
    return <div className="flex items-center justify-center py-16">
        <CustomLoader size="sm" />
      </div>;
  }
  if (featuredProducts.length === 0) {
    return null;
  }
  return <div className="relative w-full mb-12">
      {/* Embla Carousel */}
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex">
          {featuredProducts.map(product => {
          const productImage = product.node.images.edges[0]?.node.url || '';
          const productPrice = product.node.priceRange.minVariantPrice;
          return <div key={product.node.id} className="flex-[0_0_100%] min-w-0">
                <div className="relative w-full h-[70vh] md:h-[80vh] group">
                  {/* Product Image */}
                  <div className="absolute inset-0 w-full h-full">
                    <img src={productImage} alt={product.node.title} className="w-full h-full object-cover" />
                  </div>


                  {/* Content Overlay */}
                  <div className="absolute inset-0 flex flex-col items-center justify-end pb-16 md:pb-20 px-4">
                    <div className="text-center space-y-4 max-w-3xl">
                      {/* Product Title */}
                      <h2 className="font-chroma md:text-5xl lg:text-6xl text-white tracking-wide uppercase text-base">
                        {product.node.title}
                      </h2>

                      {/* Price */}
                      <p className="text-white/90 text-lg md:text-xl font-light">
                        {productPrice.currencyCode === 'USD' ? '$' : productPrice.currencyCode} {parseFloat(productPrice.amount).toFixed(2)}
                      </p>

                      {/* CTA Link */}
                      <div className="flex justify-center mt-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <button 
                          onClick={() => navigate(`/product/${product.node.handle}`)} 
                          className="font-chroma text-sm text-white underline hover:text-white/80 transition-colors uppercase tracking-wide"
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>;
        })}
        </div>
      </div>

      {/* Navigation Arrows - Desktop Only */}
      {featuredProducts.length > 1 && <>
          <Button variant="ghost" size="icon" className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 hover:text-white transition-all opacity-0 hover:opacity-100 group-hover:opacity-100" onClick={scrollPrev}>
            <ChevronLeft className="h-6 w-6" />
          </Button>
          <Button variant="ghost" size="icon" className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 hover:text-white transition-all opacity-0 hover:opacity-100 group-hover:opacity-100" onClick={scrollNext}>
            <ChevronRight className="h-6 w-6" />
          </Button>
        </>}

      {/* Dot Indicators */}
      {featuredProducts.length > 1 && <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-10">
          {featuredProducts.map((_, index) => <button key={index} className={`h-2 rounded-full transition-all duration-300 ${index === selectedIndex ? 'w-8 bg-white' : 'w-2 bg-white/40 hover:bg-white/60'}`} onClick={() => scrollTo(index)} aria-label={`Go to slide ${index + 1}`} />)}
        </div>}
    </div>;
};